import { Map } from 'immutable';
import React, { useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import {
    getChatSessionDetails,
    getSessionContent,
    getSessionContentHistory,
} from 'flow/actions/chat-actions';
import { MessageFileSkeleton } from 'flow/components/loaders/MessageFileSkeleton';
import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectSessionFiles } from 'flow/selectors/chat-message-selectors';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getAttachmentLink } from 'flow/utils';
import {
    ChatContent,
    fileSizeConverter,
    getAttachmentTypeFromExtension,
    getImageFromChatAttachment,
} from 'flow/utils/message-utils';
import moment from 'moment';

const MessagesFilesPage = () => {
    const { i18next } = useAppContext();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const fileDetails = useAppSelector((state) =>
        state.chats.getIn(['details', id], Map()),
    );

    const storedFiles = useAppSelector((state) =>
        selectSessionFiles(state, id),
    );
    const files = useMemo(() => storedFiles?.reverse(), [storedFiles]);

    useEffect(() => {
        dispatch(getChatSessionDetails(id));
        dispatch(getSessionContent(id, 'files'));
    }, [dispatch, id]);

    if (!files) {
        return <Loading />;
    }

    return (
        <Page
            header={i18next.t('Chat.Details.Files')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['messages.id.settings'], { id }),
                )
            }
            mainId="main-scrollable-div"
        >
            <InfiniteScroll
                dataLength={files.size}
                next={() => {
                    dispatch(
                        getSessionContentHistory(
                            id,
                            files.last()?.get('key'),
                            ChatContent.FILES,
                        ),
                    );
                }}
                hasMore={files.size < fileDetails?.get('numberOfFiles')}
                loader={<MessageFileSkeleton />}
                scrollableTarget="main-scrollable-div"
            >
                {files
                    .map((file) => {
                        const attachment = file.get('attachment');
                        const fileType = getAttachmentTypeFromExtension(
                            attachment.get('fileName'),
                        );

                        return (
                            <div
                                key={file.get('key')}
                                className="group flex h-13 w-full items-center gap-x-3 rounded-sm p-2 hover:bg-gray-200"
                            >
                                <div>
                                    <Icon
                                        iconKey={getImageFromChatAttachment(
                                            fileType as any,
                                        )}
                                        size="large"
                                    />
                                </div>

                                <div className="flex-1 truncate">
                                    {attachment.get('fileName')}
                                </div>

                                <div className="reveal-on-hover-or-focus flex items-center gap-x-3 text-sm text-gray-500">
                                    <div>
                                        {fileSizeConverter(
                                            attachment.get('fileSize'),
                                        )}
                                    </div>

                                    <a
                                        className="hover:darken-10 flex items-center justify-center rounded-sm border-none p-1 text-gray-600"
                                        href={getAttachmentLink(id, attachment)}
                                        download={true}
                                        aria-label={i18next.t(
                                            'Tooltip.Audio.Download',
                                        )}
                                    >
                                        <Icon iconKey="download" />
                                    </a>
                                </div>

                                <div className="group-focus-visible-within:hidden block text-sm text-gray-500 group-hover:hidden">
                                    {moment(file.get('sent'))
                                        .locale(i18next.language)
                                        .format('D MMM')}
                                </div>
                            </div>
                        );
                    })
                    .toArray()}
            </InfiniteScroll>
        </Page>
    );
};

export default MessagesFilesPage;
